import { useMemo } from 'react';
import styled from 'styled-components';

import { LoadingBoundary } from '@/common/components/LoadingBoundary';
import { ConnectorType } from '@/common/dto/connectors/Interface';
import { useResource } from '@/common/resources';
import { DocTitle } from '@/common/utils/DocTitleUtils';
import { connectApp } from '@/common/utils/connect-app';
import { getConnectorIcon } from '@/common/utils/connector-helpers';
import { getConnectedApps } from '@/common/utils/get-connected-apps';
import { useGetUserInfoQuery } from '@/store/auth';
import { filterByConnectorType, useGetConnectorsQuery } from '@/store/connectors';
import { useGetUserConnectorsQuery } from '@/store/user-connectors';

import { ConnectedApps } from './components/ConnectedApps';
import { IConnectedAppItemActions } from './components/ConnectedApps/components/ConnectedAppItemActions/interface';

const StyledDiv = styled.div`
  padding: 28px;
  flex: 1 0 auto;
`;

const StyledLoadingBoundary = styled(LoadingBoundary)`
  flex-basis: 100%;
`;

const ConnectedAppsView = () => {
  const { docTitle } = useResource((resource) => ({
    docTitle: resource.APP_SETTINGS.CONNECTED_APPS.docTitle,
  }));

  const { data: connectors = [], isFetching: isFetchingConnectors } = useGetConnectorsQuery();
  const { data: userConnectors = [], isFetching: isFetchingUserConnectors } =
    useGetUserConnectorsQuery();
  const { data: userInfo } = useGetUserInfoQuery();

  const apps = useMemo(
    () =>
      getConnectedApps(filterByConnectorType(connectors, ConnectorType.CONNECTOR), userConnectors),
    [connectors, userConnectors]
  );

  const connectedAppItemActionsList: IConnectedAppItemActions[] = apps.map(
    ({ name, userConnectorId, description, connectorId, authType, isConnected }) => ({
      icon: getConnectorIcon(connectorId),
      userConnectorId,
      connectorId,
      authType,
      details: description,
      name,
      isConnected,
      editApp: (connectorId) => connectApp(connectorId, userInfo?.platformRoles ?? []),
    })
  );

  return (
    <StyledLoadingBoundary
      isLoading={isFetchingConnectors || isFetchingUserConnectors}
      transparent={false}
    >
      <DocTitle>{docTitle}</DocTitle>
      <StyledDiv>
        <ConnectedApps apps={connectedAppItemActionsList} />
      </StyledDiv>
    </StyledLoadingBoundary>
  );
};

export { ConnectedAppsView };
